import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { depreciationRateApi, DepreciationRateRequest } from '../../api/depreciationRate.api';
import { categoryApi, Category } from '../../api/category.api';

const initialForm: DepreciationRateRequest = {
  categoryId: 0,
  assetType: '',
  financialYear: '',
  depreciationPercentage: 0,
  depreciationMethod: '',
  usefulLifeYears: undefined,
  residualValuePercentage: undefined,
  effectiveFromDate: '',
  effectiveToDate: '',
};

// Financial year list generator
const getPastAndCurrentFinancialYears = (startYear: number) => {
  const years: string[] = [];
  const today = new Date();
  let currentFYStart = today.getFullYear();
  if (today.getMonth() < 3) currentFYStart--;

  for (let fy = startYear; fy <= currentFYStart; fy++) {
    const fyEnd = (fy + 1).toString().slice(-2);
    years.push(`${fy}-${fyEnd}`);
  }
  return years;
};
const financialYears = getPastAndCurrentFinancialYears(2020);

const EditDepreciationRate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<DepreciationRateRequest>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);

  // ⭐ AUTO-FILL FUNCTION
  const handleFinancialYearAutoFill = (fy: string) => {
    if (!fy.includes("-")) return;

    const [start, end] = fy.split("-");
    const startYear = Number(start);
    const endYearFull = Number(start.slice(0, 2) + end);

    const effectiveFrom = `${startYear}-04-01`;
    const effectiveTo = `${endYearFull}-03-31`;

    setForm(prev => ({
      ...prev,
      financialYear: fy,
      effectiveFromDate: effectiveFrom,
      effectiveToDate: effectiveTo,
    }));
  };

  // Load old data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setFetching(true);
      try {
        const response = await depreciationRateApi.getDepreciationRateById(Number(id));
        const data = response.data;

        setForm({
          categoryId: data.categoryId,
          assetType: data.assetType || '',
          financialYear: data.financialYear,
          depreciationPercentage: data.depreciationPercentage,
          depreciationMethod: data.depreciationMethod,
          usefulLifeYears: data.usefulLifeYears,
          residualValuePercentage: data.residualValuePercentage,
          effectiveFromDate: data.effectiveFromDate,
          effectiveToDate: data.effectiveToDate || '',
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch depreciation rate');
      } finally {
        setFetching(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    // Fetch categories for dropdown
    const fetchCategories = async () => {
      try {
        const data = await categoryApi.getCategories();
        setCategories(data || []);
      } catch {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.categoryId) errs.categoryId = 'Category ID is required';
    if (!form.financialYear) errs.financialYear = 'Financial year is required';
    if (!form.depreciationPercentage || form.depreciationPercentage < 0 || form.depreciationPercentage > 100)
      errs.depreciationPercentage = 'Depreciation % must be 0-100';
    if (!form.depreciationMethod) errs.depreciationMethod = 'Method is required';
    if (!form.effectiveFromDate) errs.effectiveFromDate = 'Effective from date is required';
    return errs;
  };

  // Input change handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "depreciationPercentage") {
      const sanitized = value.replace(/^0+(?=\d)/, '');
      setForm(prev => ({ ...prev, [name]: Number(sanitized) }));
    } else if (name === 'categoryId' || name === 'usefulLifeYears' || name === 'residualValuePercentage') {
      setForm(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // Save
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0 || !id) return;

    setLoading(true);
    setError(null);

    try {
      await depreciationRateApi.updateDepreciationRate(Number(id), form);
      navigate('/depreciation-rates');
    } catch (err: any) {
      setError(err.message || 'Failed to update depreciation rate');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-xl mx-auto bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-semibold mb-6 text-gray-800">Edit Depreciation Rate</h1>

        {error && <div className="text-red-600 mb-4">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* CATEGORY */}
          <div>
            <label className="block text-sm mb-1">Category</label>
            <select
              name="categoryId"
              value={form.categoryId}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded ${errors.categoryId ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select category</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {errors.categoryId && <p className="text-sm text-red-600">{errors.categoryId}</p>}
          </div>

          {/* ASSET TYPE */}
          <div>
            <label className="block text-sm mb-1">Asset Type (optional)</label>
            <input
              type="text"
              name="assetType"
              value={form.assetType || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded border-gray-300"
            />
          </div>

          {/* FINANCIAL YEAR */}
          <div>
            <label className="block text-sm mb-1">Financial Year</label>
            <select
              name="financialYear"
              value={form.financialYear}
              onChange={(e) => {
                handleFinancialYearAutoFill(e.target.value);
              }}
              className={`w-full px-3 py-2 border rounded ${errors.financialYear ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select financial year</option>
              {financialYears.map(fy => (
                <option key={fy} value={fy}>{fy}</option>
              ))}
            </select>
            {errors.financialYear && <p className="text-sm text-red-600">{errors.financialYear}</p>}
          </div>

          {/* DEPRECIATION % */}
          <div>
            <label className="block text-sm mb-1">Depreciation %</label>
            <input
              type="number"
              name="depreciationPercentage"
              value={form.depreciationPercentage}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded ${errors.depreciationPercentage ? 'border-red-500' : 'border-gray-300'}`}
              min={0}
              max={100}
              step={0.01}
            />
            {errors.depreciationPercentage && <p className="text-sm text-red-600">{errors.depreciationPercentage}</p>}
          </div>

          {/* METHOD */}
          <div>
            <label className="block text-sm mb-1">Method</label>
            <select
              name="depreciationMethod"
              value={form.depreciationMethod}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded ${errors.depreciationMethod ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select method</option>
              <option value="PRO_RATA">PRO_RATA (Pro-rata/Time Apportioned)</option>
              {/* <option value="SLM">SLM (Straight Line)</option>
              <option value="WDV">WDV (Written Down Value)</option> */}
            </select>
            {errors.depreciationMethod && <p className="text-sm text-red-600">{errors.depreciationMethod}</p>}
          </div>

          {/* USEFUL LIFE */}
          <div>
            <label className="block text-sm mb-1">Useful Life (years)</label>
            <input
              type="number"
              name="usefulLifeYears"
              value={form.usefulLifeYears || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded border-gray-300"
            />
          </div>

          {/* RESIDUAL VALUE */}
          <div>
            <label className="block text-sm mb-1">Residual Value %</label>
            <input
              type="number"
              name="residualValuePercentage"
              value={form.residualValuePercentage || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded border-gray-300"
            />
          </div>

          {/* EFFECTIVE FROM */}
          <div>
            <label className="block text-sm mb-1">Effective From</label>
            <input
              type="date"
              name="effectiveFromDate"
              value={form.effectiveFromDate}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded ${errors.effectiveFromDate ? 'border-red-500' : 'border-gray-300'}`}
            />
          </div>

          {/* EFFECTIVE TO */}
          <div>
            <label className="block text-sm mb-1">Effective To</label>
            <input
              type="date"
              name="effectiveToDate"
              value={form.effectiveToDate || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded border-gray-300"
            />
          </div>

          {/* BUTTONS */}
          <div className="flex justify-between pt-4">
            <button
              type="button"
              onClick={() => navigate('/depreciation-rates')}
              className="px-4 py-2 border rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded"
            >
              {loading ? 'Updating...' : 'Update Rate'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditDepreciationRate; 